/**
 * Fila de documento de la dirección v2 (DESIGN.md §11.2, maquetas m1-lista y m3-tta): título en una línea, una frase
 * de hasta dos líneas y la columna de datos alineada a la derecha. Una sola fila para las cuatro bibliotecas y
 * Favoritos (§5).
 *
 * - Clic: abre el documento en la pantalla (`onAbrir`); Ctrl/⌘ + clic o clic medio: pestaña nueva (es un enlace).
 * - En hover o con foco, guardar, copiar cita y abrir como página flotan a la izquierda de la columna de datos, con
 *   su lugar reservado: no la mueven.
 * - Como máximo una señal de color (`senal`); «Cambia un criterio» va en gris porque es información.
 */
import { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Copy, ExternalLink, History, Star } from 'lucide-react';
import Resaltado from './Resaltado';
import { Barra, FOCO } from './piezas';
import type { CollectionKey } from '../../utils/institutions';
import { Agrupacion, DocFila, agrupar, datosFila, fraseFila, senalFila, tituloFila, tramosNorma } from '../../utils/filaDocumento';

// Sin relleno lateral: una coincidencia dentro de una palabra («crédito|s») no la parte.
const MARCA = 'bg-indigo-50 text-indigo-700 rounded-[3px]';

/** Ancho de la columna de datos: 150 px en el SII y las leyes; 230 px en el TTA (tribunal y RIT). */
export const anchoDatos = (coleccion: CollectionKey) => (coleccion === 'jud' ? 230 : 150);

/** «Agrícola Ariztía Ltda. con SII»: la parte que no es el SII va en 600 y el resto en 400 (maqueta m3). */
function Caratula({ texto, consulta }: { texto: string; consulta?: string }) {
  const m = /^(.*?)\s+con\s+(.*)$/i.exec(texto);
  if (!m) return <Resaltado texto={texto} consulta={consulta} marca={MARCA} />;
  const esSii = (s: string) => /^(SII|Servicio de Impuestos)/i.test(s.trim());
  const fuerte = esSii(m[1]) && !esSii(m[2]) ? 2 : 1;
  const parte = (s: string, i: 1 | 2) => (
    <span className={fuerte === i ? 'font-semibold' : 'font-normal'}><Resaltado texto={s} consulta={consulta} marca={MARCA} /></span>
  );
  return <>{parte(m[1], 1)}<span className="font-normal"> con </span>{parte(m[2], 2)}</>;
}

export interface FilaDocumentoProps {
  doc: DocFila;
  coleccion: CollectionKey;
  /** Página del documento (Ctrl + clic y «abrir como página»). */
  href: string;
  consulta?: string;
  compacta?: boolean;
  favorito: boolean;
  activa?: boolean;
  /** Convenios: el país delante de la clase (false cuando la lista ya agrupa por país). */
  conPais?: boolean;
  /** Línea gris de la columna en lugar de la propia de la biblioteca (Favoritos: «guardado hoy»). */
  notaColumna?: string;
  /** Ancho de la columna de datos; por defecto el de la biblioteca. Una lista mixta (Favoritos) usa uno solo. */
  anchoColumna?: number;
  /** La única señal de color de la fila (p. ej. «No vigente»); null o ausente: ninguna. */
  senal?: ReactNode;
  onAbrir: () => void;
  onFavorito: () => void;
  onCopiar: () => void;
}

export default function FilaDocumento({ doc, coleccion, href, consulta, compacta = false, favorito, activa = false, conPais = true, notaColumna, anchoColumna, senal, onAbrir, onFavorito, onCopiar }: FilaDocumentoProps) {
  const titulo = tituloFila(doc, conPais);
  const frase = compacta ? null : fraseFila(doc);
  const propios = datosFila(doc, coleccion);
  const datos = notaColumna ? { ...propios, nota: notaColumna } : propios;
  const ancho = anchoColumna ?? anchoDatos(coleccion);
  const propia = senalFila(doc);
  const abrir = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return; // el navegador abre otra pestaña
    e.preventDefault();
    onAbrir();
  };
  const accion = `w-[30px] h-[30px] rounded-[7px] grid place-items-center bg-white ring-1 ring-slate-200 text-slate-500 hover:text-slate-900 transition-colors motion-reduce:transition-none ${FOCO}`;

  return (
    <article
      className={`group relative grid gap-x-10 -mx-4 px-4 border-b transition-colors motion-reduce:transition-none ${compacta ? 'py-3' : 'py-4'} ${
        activa ? 'bg-slate-50 rounded-[10px] border-transparent' : 'border-slate-100 hover:bg-slate-50 hover:rounded-[10px] hover:border-transparent focus-within:bg-slate-50 focus-within:rounded-[10px]'
      } has-[.fila-presion:active]:bg-indigo-50`}
      style={{ gridTemplateColumns: `minmax(0,1fr) ${ancho}px` }}
    >
      <div className="min-w-0">
        <h3 className={`max-w-[min(700px,calc(100%-110px))] truncate text-[15.5px] leading-[1.4] tracking-[-0.005em] text-slate-900 ${coleccion === 'jud' ? '' : 'font-semibold'}`}>
          <Link
            to={href}
            onClick={abrir}
            title={titulo}
            data-fila={doc.id}
            className="fila-presion focus-visible:outline-none after:absolute after:inset-0 after:rounded-[10px] focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-offset-0 focus-visible:after:outline-indigo-600"
          >
            {coleccion === 'jud' ? <Caratula texto={titulo} consulta={consulta} /> : <Resaltado texto={titulo} consulta={consulta} marca={MARCA} />}
          </Link>
        </h3>
        {frase && (
          <p className={`mt-1 max-w-[700px] line-clamp-2 text-[14.5px] leading-[1.55] ${frase.sinSintesis ? 'text-slate-500' : 'text-slate-600'}`}>
            {frase.sinSintesis && <span className="mr-1.5 text-[12px] font-medium text-slate-500 border border-slate-200 rounded-[5px] px-1.5 py-px align-[1px]">sin síntesis</span>}
            {tramosNorma(frase.texto).map((t, i) =>
              t.norma && !frase.sinSintesis
                ? <span key={i} className="font-medium text-slate-900"><Resaltado texto={t.texto} consulta={consulta} marca={MARCA} /></span>
                : <Resaltado key={i} texto={t.texto} consulta={consulta} marca={MARCA} />
            )}
          </p>
        )}
      </div>

      {/* Acciones en hover: a la izquierda de la columna, sin moverla; el título deja 110 px libres para que no lo tapen */}
      <div
        className={`absolute z-10 flex gap-0.5 pl-2 bg-slate-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto transition-opacity duration-150 motion-reduce:transition-none ${compacta ? 'top-2' : 'top-3'}`}
        style={{ right: ancho + 56 }}
      >
        <button type="button" onClick={onFavorito} aria-pressed={favorito} aria-label={`Guardar en favoritos: ${titulo}`} title={favorito ? 'Quitar de favoritos' : 'Guardar'} className={accion}>
          <Star className={`w-3.5 h-3.5 ${favorito ? 'fill-indigo-600 text-indigo-600' : ''}`} />
        </button>
        <button type="button" onClick={onCopiar} aria-label={`Copiar cita: ${titulo}`} title="Copiar cita" className={accion}>
          <Copy className="w-3.5 h-3.5" />
        </button>
        <Link to={href} aria-label={`Abrir como página: ${titulo}`} title="Abrir como página" className={accion}>
          <ExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className={`flex items-end text-right tabular-nums pt-px ${compacta ? 'flex-row justify-end gap-1.5 items-baseline' : 'flex-col gap-[3px]'}`}>
        <span className="text-[13px] font-medium text-slate-700">{datos.a}</span>
        {datos.b && <span className="text-[12.5px] text-slate-500">{compacta && <span aria-hidden="true">· </span>}{datos.b}</span>}
        {!compacta && datos.nota && (
          <span className="text-[12.5px] text-slate-500 flex items-center gap-[5px]">{!notaColumna && <History className="w-3 h-3" aria-hidden="true" />}{datos.nota}</span>
        )}
        {senal ?? (propia && (
          <span className="text-[12.5px] font-medium text-amber-700 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-600" aria-hidden="true" />{propia.texto}
          </span>
        ))}
      </div>
    </article>
  );
}

/** Fila en estado «cargando»: la estructura sin texto (DESIGN.md §4). */
export function FilaCargando({ coleccion, i = 0, compacta = false }: { coleccion: CollectionKey; i?: number; compacta?: boolean }) {
  return (
    <div className={`grid gap-x-10 border-b border-slate-100 ${compacta ? 'py-3' : 'py-4'}`} style={{ gridTemplateColumns: `minmax(0,1fr) ${anchoDatos(coleccion)}px` }} aria-hidden="true">
      <div className="space-y-2.5 max-w-[700px]">
        <Barra ancho={`${62 - (i % 3) * 8}%`} alto="h-4" />
        {!compacta && <><Barra ancho="96%" alto="h-3.5" /><Barra ancho={`${70 - (i % 4) * 6}%`} alto="h-3.5" /></>}
      </div>
      <div className="flex flex-col items-end gap-2 pt-0.5"><Barra ancho="70%" alto="h-3.5" /><Barra ancho="40%" /></div>
    </div>
  );
}

/** Rótulo de grupo por fecha («Semana del 13 de octubre», «Septiembre de 2026»), 12/500 `slate-500`. */
export function RotuloGrupo({ children, pais = false }: { children: ReactNode; pais?: boolean }) {
  // Convenios agrupan por país con un rótulo más fuerte (13,5/600 `slate-800`, m7).
  return <h2 className={`pt-[22px] pb-1.5 border-b border-slate-100 ${pais ? 'text-[13.5px] font-semibold text-slate-800' : 'text-[12px] font-medium text-slate-500'}`}>{children}</h2>;
}

/** Lista de filas, agrupada por fecha si `agrupacion` viene (la lista debe llegar ordenada por fecha). */
export function ListaFilas<T extends DocFila>({ docs, agrupacion, fila }: { docs: T[]; agrupacion: Agrupacion | null; fila: (doc: T) => ReactNode }) {
  if (!agrupacion) return <div className="border-t border-slate-100">{docs.map(fila)}</div>;
  return (
    <>
      {agrupar(docs, agrupacion).map(g => (
        <section key={`${g.rotulo}-${g.docs[0].id}`} aria-label={g.rotulo}>
          <RotuloGrupo pais={agrupacion === 'pais'}>{g.rotulo}</RotuloGrupo>
          {g.docs.map(fila)}
        </section>
      ))}
    </>
  );
}
